import type { RuleSchema } from "../../studio/schemas/rule-schema.js";
import { RuleActionType, RuleOperator } from "../../studio/schemas/rule-schema.js";

const template = document.createElement("template");
template.innerHTML = `
  <div style="display: flex; height: 100%; width: 100%;">
    <main style="flex: 1; padding: var(--space-6); overflow-y: auto; background: var(--color-graphite-50);">
      <div id="canvas" style="width: 100%; max-width: 800px; margin: 0 auto; background: var(--color-surface-primary); min-height: 100%; border: 1px solid var(--color-graphite-300); border-radius: var(--radius-md); padding: var(--space-8); box-shadow: var(--shadow-sm);">
        <h2 style="font-size: var(--font-size-lg); color: var(--color-text-muted); margin-bottom: var(--space-4);">Declarative Rules</h2>
        
        <div style="margin-bottom: var(--space-4);">
          <strong style="color: var(--color-text-muted);">IF</strong>
          <div id="conditions-container" style="margin-top: var(--space-2); padding: var(--space-4); background: var(--color-graphite-50); border-radius: var(--radius-md); border: 1px solid var(--color-graphite-300);">
            <div style="display: flex; gap: 10px; align-items: center;">
               <input type="text" id="cond-field" placeholder="Field Code" style="padding: 4px;">
               <select id="cond-op" style="padding: 4px;">
                 <option value="EQUALS">EQUALS</option>
                 <option value="NOT_EQUALS">NOT_EQUALS</option>
                 <option value="GREATER_THAN">GREATER_THAN</option>
               </select>
               <input type="text" id="cond-val" placeholder="Value" style="padding: 4px;">
            </div>
          </div>
        </div>

        <div>
          <strong style="color: var(--color-text-muted);">THEN</strong>
          <div id="actions-container" style="margin-top: var(--space-2); padding: var(--space-4); background: var(--color-graphite-50); border-radius: var(--radius-md); border: 1px solid var(--color-graphite-300);">
             <div style="display: flex; gap: 10px; align-items: center;">
               <select id="act-type" style="padding: 4px;">
                 <option value="SHOW">SHOW</option>
                 <option value="HIDE">HIDE</option>
                 <option value="REQUIRE">REQUIRE</option>
                 <option value="SET_VALUE">SET_VALUE</option>
               </select>
               <input type="text" id="act-field" placeholder="Target Field Code" style="padding: 4px;">
               <input type="text" id="act-val" placeholder="Value (if Set Value)" style="padding: 4px;">
             </div>
          </div>
        </div>
      </div>
    </main>
  </div>
`;

export class RuleBuilder extends HTMLElement {
  private schema: RuleSchema = { 
    conditions: { logicalOperator: "AND", conditions: [] }, 
    actions: [] 
  };
  private onSchemaChangeCallback: ((schema: RuleSchema) => void) | null = null;

  constructor() {
    super();
    this.appendChild(template.content.cloneNode(true));
  }

  public connectedCallback() {
    // Simple 1-to-1 UI for demonstration without complex AST nesting in the UI
    const condField = this.querySelector("#cond-field") as HTMLInputElement;
    const condOp = this.querySelector("#cond-op") as HTMLSelectElement;
    const condVal = this.querySelector("#cond-val") as HTMLInputElement;
    
    const actType = this.querySelector("#act-type") as HTMLSelectElement;
    const actField = this.querySelector("#act-field") as HTMLInputElement;
    const actVal = this.querySelector("#act-val") as HTMLInputElement;

    const sync = () => {
      this.schema.conditions.conditions = [{
        fieldCode: condField.value,
        operator: condOp.value as RuleOperator,
        value: condVal.value
      } as any]; // Casting for simplicity in this structural UI

      this.schema.actions = [{
        type: actType.value as RuleActionType,
        targetFieldCode: actField.value,
        value: actVal.value
      }];
      
      this.notifyChange();
    };

    [condField, condOp, condVal, actType, actField, actVal].forEach(el => {
      el.addEventListener("input", sync);
      el.addEventListener("change", sync);
    });
  }

  public setSchema(schema: RuleSchema) {
    this.schema = JSON.parse(JSON.stringify(schema));
    if (!this.schema.conditions) this.schema.conditions = { logicalOperator: "AND", conditions: [] };
    if (!this.schema.actions) this.schema.actions = [];
    
    // Bind back to simple UI
    if (this.schema.conditions.conditions.length > 0) {
      const cond = this.schema.conditions.conditions[0] as any;
      if (cond.fieldCode) (this.querySelector("#cond-field") as HTMLInputElement).value = cond.fieldCode;
      if (cond.operator) (this.querySelector("#cond-op") as HTMLSelectElement).value = cond.operator;
      if (cond.value) (this.querySelector("#cond-val") as HTMLInputElement).value = cond.value;
    }

    if (this.schema.actions.length > 0) {
      const act = this.schema.actions[0];
      if (act && act.type) (this.querySelector("#act-type") as HTMLSelectElement).value = act.type;
      if (act && act.targetFieldCode) (this.querySelector("#act-field") as HTMLInputElement).value = act.targetFieldCode;
      if (act && act.value) (this.querySelector("#act-val") as HTMLInputElement).value = act.value;
    }
  }

  public onSchemaChange(callback: (schema: RuleSchema) => void) {
    this.onSchemaChangeCallback = callback;
  }

  private notifyChange() {
    if (this.onSchemaChangeCallback) {
      this.onSchemaChangeCallback(JSON.parse(JSON.stringify(this.schema)));
    }
  }
}
if (!customElements.get("app-rule-builder")) {
  customElements.define("app-rule-builder", RuleBuilder);
}

